import React, { useState, useEffect, useContext } from "react";
import { ThemeContext } from "../../context";
import PreviewFull from "./PreviewFull";
import "./admin.css";

const AdminDashboard = ({ onLogout }) => {
    const theme = useContext(ThemeContext);
    const darkMode = theme.state.darkMode;

    // State for data
    const [projects, setProjects] = useState([]);
    const [experience, setExperience] = useState([]);
    const [skills, setSkills] = useState([]);
    const [content, setContent] = useState({
        name: "",
        title: "",
        description: "",
        tags: [],
        about_title: "",
        about_subtitle: "",
        about_paragraphs: [],
        contact_title: "",
        contact_subtitle: "",
        contact_note: "",
        socials: []
    });

    // State for UI
    const [expandedSections, setExpandedSections] = useState({
        content: true,
        projects: false,
        experience: false,
        skills: false
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState(""); // 'success' or 'error'
    const [showPreview, setShowPreview] = useState(true);
    const [dividerPos, setDividerPos] = useState(50); // 50% split
    const [isDragging, setIsDragging] = useState(false);

    // Load data on mount
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [projectsRes, experienceRes, skillsRes, contentRes] = await Promise.all([
                fetch("/products.json"),
                fetch("/experience.json"),
                fetch("/skills.json"),
                fetch("/content.json")
            ]);

            const projectsData = await projectsRes.json();
            const experienceData = await experienceRes.json();
            const skillsData = await skillsRes.json();
            const contentData = await contentRes.json();

            setProjects(projectsData);
            setExperience(experienceData);
            setSkills(skillsData);
            setContent(contentData);
        } catch (error) {
            showMessage("Error loading data: " + error.message, "error");
        }
    };

    const showMessage = (msg, type) => {
        setMessage(msg);
        setMessageType(type);
        setTimeout(() => {
            setMessage("");
        }, 4000);
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleSave = async (type) => {
        setLoading(true);
        try {
            let dataToSave = {};

            if (type === "projects") {
                dataToSave = { type: "projects", data: projects };
            } else if (type === "experience") {
                dataToSave = { type: "experience", data: experience };
            } else if (type === "skills") {
                dataToSave = { type: "skills", data: skills };
            } else if (type === "content") {
                dataToSave = { type: "content", data: content };
            }

            const response = await fetch("/.netlify/functions/update-json", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(dataToSave)
            });

            const result = await response.json();

            if (response.ok) {
                showMessage("Changes saved. Site will rebuild shortly.", "success");
            } else {
                showMessage(`Error: ${result.error || "Failed to save"}`, "error");
            }
        } catch (error) {
            showMessage("Error saving data: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleAddProject = () => {
        const newProject = {
            id: Math.max(...projects.map(p => p.id), 0) + 1,
            title: "New Project",
            description: "Project description",
            img: "",
            link: ""
        };
        setProjects([...projects, newProject]);
    };

    const handleDeleteProject = (id) => {
        setProjects(projects.filter(p => p.id !== id));
    };

    const handleProjectChange = (id, field, value) => {
        setProjects(projects.map(p =>
            p.id === id ? { ...p, [field]: value } : p
        ));
    };

    const handleAddExperience = () => {
        const newExp = {
            company: "Company Name",
            role: "Job Title",
            duration: "Start – End",
            desc: "Description",
            color: "arcade-cyan"
        };
        setExperience([...experience, newExp]);
    };

    const handleDeleteExperience = (index) => {
        setExperience(experience.filter((_, i) => i !== index));
    };

    const handleExperienceChange = (index, field, value) => {
        setExperience(experience.map((exp, i) =>
            i === index ? { ...exp, [field]: value } : exp
        ));
    };

    const handleAddSkill = () => {
        const newSkill = {
            name: "New Skill",
            category: "core"
        };
        setSkills([...skills, newSkill]);
    };

    const handleDeleteSkill = (index) => {
        setSkills(skills.filter((_, i) => i !== index));
    };

    const handleSkillChange = (index, field, value) => {
        setSkills(skills.map((skill, i) =>
            i === index ? { ...skill, [field]: value } : skill
        ));
    };

    // Resizable divider handlers
    const handleMouseDown = () => {
        setIsDragging(true);
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;
            const container = document.querySelector('.admin-container-resizable');
            if (!container) return;
            const containerRect = container.getBoundingClientRect();
            const newPos = ((e.clientX - containerRect.left) / containerRect.width) * 100;
            if (newPos > 30 && newPos < 70) {
                setDividerPos(newPos);
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging]);

    return (
        <div className={`admin-dashboard ${darkMode ? 'dark' : 'light'}`}>
            <div className="admin-header">
                <div className="admin-header-left">
                    <h1>Portfolio Admin</h1>
                    <p>Edit your portfolio content directly</p>
                </div>
                <div className="admin-header-right">
                    <button 
                        className={`btn-toggle-preview ${showPreview ? 'active' : ''}`}
                        onClick={() => setShowPreview(!showPreview)}
                        title={showPreview ? "Hide Preview" : "Show Preview"}
                    >
                        {showPreview ? '👁 Hide Preview' : '👁️ Show Preview'}
                    </button>
                    <button className="btn-logout" onClick={onLogout}>
                        Logout
                    </button>
                </div>
            </div>

            {message && (
                <div className={`message-banner ${messageType}`}>
                    {messageType === "success" && "✓ "}
                    {messageType === "error" && "✗ "}
                    {message}
                </div>
            )}

            <div className={`admin-container-resizable ${showPreview ? 'split' : 'full'}`}>
                {/* Left Panel - Collapsible Sections */}
                <div className="admin-editor" style={{width: showPreview ? `${dividerPos}%` : '100%'}}>
                    {/* Content Section */}
                    <div className="collapsible-section">
                        <div className="section-header-collapsible" onClick={() => toggleSection('content')}>
                            <div className="header-content">
                                <span className="toggle-icon">{expandedSections.content ? '▼' : '▶'}</span>
                                <h2>👋 Content & Settings</h2>
                            </div>
                            <button className="btn-save-section" onClick={(e) => { e.stopPropagation(); handleSave('content'); }} disabled={loading}>
                                {loading ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                        {expandedSections.content && (
                            <div className="section-content">
                                <div className="content-section">
                                    <h3>👤 Intro Section</h3>
                                    <div className="form-group">
                                        <label>Your Name</label>
                                        <input
                                            type="text"
                                            value={content.name}
                                            onChange={(e) => setContent({...content, name: e.target.value})}
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Your Title</label>
                                        <input
                                            type="text"
                                            value={content.title}
                                            onChange={(e) => setContent({...content, title: e.target.value})}
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Your Description</label>
                                        <textarea
                                            value={content.description}
                                            onChange={(e) => setContent({...content, description: e.target.value})}
                                            className="form-textarea"
                                            rows="4"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Tags (comma-separated)</label>
                                        <input
                                            type="text"
                                            value={content.tags ? content.tags.join(', ') : ''}
                                            onChange={(e) => setContent({...content, tags: e.target.value.split(',').map(t => t.trim())})}
                                            className="form-input"
                                            placeholder="e.g., Unity, Game Dev, C#"
                                        />
                                    </div>
                                </div>

                                <div className="content-section">
                                    <h3>📖 About Section</h3>
                                    <div className="form-group">
                                        <label>About Title</label>
                                        <input
                                            type="text"
                                            value={content.about_title}
                                            onChange={(e) => setContent({...content, about_title: e.target.value})}
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>About Subtitle</label>
                                        <input
                                            type="text"
                                            value={content.about_subtitle}
                                            onChange={(e) => setContent({...content, about_subtitle: e.target.value})}
                                            className="form-input"
                                        />
                                    </div>
                                    {content.about_paragraphs && content.about_paragraphs.map((para, idx) => (
                                        <div key={idx} className="form-group">
                                            <label>Paragraph {idx + 1}</label>
                                            <textarea
                                                value={para}
                                                onChange={(e) => {
                                                    const newParas = [...content.about_paragraphs];
                                                    newParas[idx] = e.target.value;
                                                    setContent({...content, about_paragraphs: newParas});
                                                }}
                                                className="form-textarea"
                                                rows="3"
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="content-section">
                                    <h3>📬 Contact Section</h3>
                                    <div className="form-group">
                                        <label>Contact Title</label>
                                        <input
                                            type="text"
                                            value={content.contact_title}
                                            onChange={(e) => setContent({...content, contact_title: e.target.value})}
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Contact Subtitle</label>
                                        <input
                                            type="text"
                                            value={content.contact_subtitle}
                                            onChange={(e) => setContent({...content, contact_subtitle: e.target.value})}
                                            className="form-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Contact Note</label>
                                        <textarea
                                            value={content.contact_note}
                                            onChange={(e) => setContent({...content, contact_note: e.target.value})}
                                            className="form-textarea"
                                            rows="2"
                                        />
                                    </div>

                                    <h4 style={{marginTop: '20px', marginBottom: '10px'}}>Social Media Links</h4>
                                    {content.socials && content.socials.map((social, idx) => (
                                        <div key={idx} className="item-card social-card">
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>Platform Name</label>
                                                    <input
                                                        type="text"
                                                        value={social.name}
                                                        onChange={(e) => {
                                                            const newSocials = [...content.socials];
                                                            newSocials[idx].name = e.target.value;
                                                            setContent({...content, socials: newSocials});
                                                        }}
                                                        className="form-input"
                                                        placeholder="e.g., LinkedIn"
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>Icon Class</label>
                                                    <input
                                                        type="text"
                                                        value={social.icon}
                                                        onChange={(e) => {
                                                            const newSocials = [...content.socials];
                                                            newSocials[idx].icon = e.target.value;
                                                            setContent({...content, socials: newSocials});
                                                        }}
                                                        className="form-input"
                                                        placeholder="e.g., fa-brands fa-linkedin"
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>URL</label>
                                                <input
                                                    type="url"
                                                    value={social.url}
                                                    onChange={(e) => {
                                                        const newSocials = [...content.socials];
                                                        newSocials[idx].url = e.target.value;
                                                        setContent({...content, socials: newSocials});
                                                    }}
                                                    className="form-input"
                                                    placeholder="https://..."
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Projects Section */}
                    <div className="collapsible-section">
                        <div className="section-header-collapsible" onClick={() => toggleSection('projects')}>
                            <div className="header-content">
                                <span className="toggle-icon">{expandedSections.projects ? '▼' : '▶'}</span>
                                <h2>🎮 Projects</h2>
                            </div>
                            <button className="btn-save-section" onClick={(e) => { e.stopPropagation(); handleSave('projects'); }} disabled={loading}>
                                {loading ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                        {expandedSections.projects && (
                            <div className="section-content">
                                <button className="btn-add" onClick={handleAddProject}>+ Add Project</button>
                                <div className="items-list">
                                    {projects.map((project) => (
                                        <div key={project.id} className="item-card">
                                            <div className="item-header">
                                                <h3>Project #{project.id}</h3>
                                                <div className="item-header-actions">
                                                    <label className="checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={project.visible !== false}
                                                            onChange={(e) => handleProjectChange(project.id, 'visible', e.target.checked)}
                                                        />
                                                        <span>Visible</span>
                                                    </label>
                                                    <button
                                                        className="btn-delete"
                                                        onClick={() => handleDeleteProject(project.id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <label>Title</label>
                                                <input
                                                    type="text"
                                                    value={project.title}
                                                    onChange={(e) => handleProjectChange(project.id, 'title', e.target.value)}
                                                    className="form-input"
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Description</label>
                                                <textarea
                                                    value={project.description}
                                                    onChange={(e) => handleProjectChange(project.id, 'description', e.target.value)}
                                                    className="form-textarea"
                                                    rows="3"
                                                />
                                            </div>

                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>Image URL</label>
                                                    <input
                                                        type="text"
                                                        value={project.img}
                                                        onChange={(e) => handleProjectChange(project.id, 'img', e.target.value)}
                                                        className="form-input"
                                                        placeholder="e.g., GameImages/image.png"
                                                    />
                                                </div>

                                                <div className="form-group">
                                                    <label>Link</label>
                                                    <input
                                                        type="text"
                                                        value={project.link}
                                                        onChange={(e) => handleProjectChange(project.id, 'link', e.target.value)}
                                                        className="form-input"
                                                        placeholder="e.g., https://example.com"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Experience Section */}
                    <div className="collapsible-section">
                        <div className="section-header-collapsible" onClick={() => toggleSection('experience')}>
                            <div className="header-content">
                                <span className="toggle-icon">{expandedSections.experience ? '▼' : '▶'}</span>
                                <h2>💼 Experience</h2>
                            </div>
                            <button className="btn-save-section" onClick={(e) => { e.stopPropagation(); handleSave('experience'); }} disabled={loading}>
                                {loading ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                        {expandedSections.experience && (
                            <div className="section-content">
                                <button className="btn-add" onClick={handleAddExperience}>+ Add Experience</button>
                                <div className="items-list">
                                    {experience.map((exp, index) => (
                                        <div key={index} className="item-card">
                                            <div className="item-header">
                                                <h3>{exp.company || `Experience #${index + 1}`}</h3>
                                                <button
                                                    className="btn-delete"
                                                    onClick={() => handleDeleteExperience(index)}
                                                >
                                                    Delete
                                                </button>
                                            </div>

                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>Company</label>
                                                    <input
                                                        type="text"
                                                        value={exp.company}
                                                        onChange={(e) => handleExperienceChange(index, 'company', e.target.value)}
                                                        className="form-input"
                                                    />
                                                </div>

                                                <div className="form-group">
                                                    <label>Role</label>
                                                    <input
                                                        type="text"
                                                        value={exp.role}
                                                        onChange={(e) => handleExperienceChange(index, 'role', e.target.value)}
                                                        className="form-input"
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-group">
                                                <label>Duration</label>
                                                <input
                                                    type="text"
                                                    value={exp.duration}
                                                    onChange={(e) => handleExperienceChange(index, 'duration', e.target.value)}
                                                    className="form-input"
                                                    placeholder="e.g., Jan 2020 – Dec 2021"
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Description</label>
                                                <textarea
                                                    value={exp.desc}
                                                    onChange={(e) => handleExperienceChange(index, 'desc', e.target.value)}
                                                    className="form-textarea"
                                                    rows="3"
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Color Theme</label>
                                                <select
                                                    value={exp.color}
                                                    onChange={(e) => handleExperienceChange(index, 'color', e.target.value)}
                                                    className="form-input"
                                                >
                                                    <option value="arcade-cyan">Cyan</option>
                                                    <option value="arcade-yellow">Yellow</option>
                                                    <option value="arcade-green">Green</option>
                                                    <option value="arcade-orange">Orange</option>
                                                    <option value="arcade-purple">Purple</option>
                                                </select>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Skills Section */}
                    <div className="collapsible-section">
                        <div className="section-header-collapsible" onClick={() => toggleSection('skills')}>
                            <div className="header-content">
                                <span className="toggle-icon">{expandedSections.skills ? '▼' : '▶'}</span>
                                <h2>⚡ Skills</h2>
                            </div>
                            <button className="btn-save-section" onClick={(e) => { e.stopPropagation(); handleSave('skills'); }} disabled={loading}>
                                {loading ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                        {expandedSections.skills && (
                            <div className="section-content">
                                <button className="btn-add" onClick={handleAddSkill}>+ Add Skill</button>
                                <div className="items-list">
                                    {skills.map((skill, index) => (
                                        <div key={index} className="item-card skill-card">
                                            <div className="item-header">
                                                <h3>{skill.name || `Skill #${index + 1}`}</h3>
                                                <div className="item-header-actions">
                                                    <label className="checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={skill.visible !== false}
                                                            onChange={(e) => handleSkillChange(index, 'visible', e.target.checked)}
                                                        />
                                                        <span>Visible</span>
                                                    </label>
                                                    <button
                                                        className="btn-delete"
                                                        onClick={() => handleDeleteSkill(index)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label>Skill Name</label>
                                                    <input
                                                        type="text"
                                                        value={skill.name}
                                                        onChange={(e) => handleSkillChange(index, 'name', e.target.value)}
                                                        className="form-input"
                                                    />
                                                </div>

                                                <div className="form-group">
                                                    <label>Category</label>
                                                    <select
                                                        value={skill.category}
                                                        onChange={(e) => handleSkillChange(index, 'category', e.target.value)}
                                                        className="form-input"
                                                    >
                                                        <option value="core">Core</option>
                                                        <option value="ui">UI/UX</option>
                                                        <option value="platform">Platform</option>
                                                        <option value="advanced">Advanced</option>
                                                        <option value="tools">Tools</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Resizable Divider */}
                {showPreview && (
                    <div 
                        className="admin-divider"
                        onMouseDown={handleMouseDown}
                        style={{cursor: isDragging ? 'col-resize' : 'ew-resize'}}
                    />
                )}

                {/* Right Panel - Live Preview - Full Site View */}
                {showPreview && (
                    <div className="admin-preview-panel-full" style={{width: `${100 - dividerPos}%`}}>
                        <PreviewFull 
                            content={content}
                            experience={experience}
                            projects={projects}
                            skills={skills}
                            darkMode={darkMode}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;
